// Copyright 2022, Imprivata, Inc.  All rights reserved.

import type { ICameraFeedback, IDeviceData } from '../models';
import { CameraFeedback } from '../models';
import type { IFaceCaptureService } from './IFaceCaptureService';

const alignFaceWithGuideMessage: string = 'Align face with the guide';

export class SimpleFaceCaptureService implements IFaceCaptureService {
  private video: HTMLVideoElement | null = null;
  private videoStream: MediaStream | null = null;
  private isInitialized = false;

  private readonly isMobile = /iPhone|iPad|iPod|Android/i.test(navigator.userAgent);

  constructor(
    private setCapturedAction: (captured: boolean) => void,
    private setCapturingAction: (capturing: boolean) => void,
    private setDeviceDataAction: (deviceData: IDeviceData) => void,
    private setFeedbackAction: (feedback: ICameraFeedback) => void,
    private enableDebugLogging = false,
  ) {}

  public async initialize(): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] initialize() called');
    }

    // Simplified initialization - just mark as initialized
    this.isInitialized = true;

    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Initialization completed');
    }

    return Promise.resolve();
  }

  public async startVideo(video: HTMLVideoElement): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] startVideo() called, isInitialized:', this.isInitialized);
    }

    if (!this.isInitialized) {
      throw new Error('Face capture service must be initialized before starting video');
    }

    this.setInstantFeedback(
      new CameraFeedback(
        alignFaceWithGuideMessage,
        'Center yourself in the frame and look at the camera',
      ),
    );

    await this.startVideoStream();

    if (!this.videoStream) {
      if (this.enableDebugLogging) {
        console.warn('[SimpleFaceCaptureService] Video stream not available');
      }
      return;
    }

    this.video = video;
    this.video.srcObject = this.videoStream;

    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Video stream attached to video element');
    }
  }

  public stopVideo(): void {
    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] stopVideo() called');
    }

    // Always stop the video stream to prevent camera from staying active
    // even if srcObject was cleared or never attached (race during startVideoStream)
    this.stopVideoStream();

    // Clear the srcObject if video element exists
    if (this.video) {
      this.video.srcObject = null;
    }

    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Video stream stopped and cleared');
    }
  }

  public async runLiveness(
    canvas: HTMLCanvasElement,
    captureWindowMs: number,
  ): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] runLiveness() started', {
        captureWindowMs,
        hasVideo: !!this.video,
      });
    }

    if (!this.video) {
      if (this.enableDebugLogging) {
        console.warn('[SimpleFaceCaptureService] No video element available');
      }
      return;
    }

    // Update feedback to indicate capturing
    this.setInstantFeedback(new CameraFeedback(alignFaceWithGuideMessage, 'Hold still, capturing'));

    this.setCapturedAction(false);
    this.setCapturingAction(true);

    if (this.enableDebugLogging) {
      console.log(`[SimpleFaceCaptureService] Waiting ${captureWindowMs}ms capture window`);
    }

    // Wait for the capture window to allow for multiple frames
    await new Promise((resolve) => setTimeout(resolve, captureWindowMs));

    // Capture the current video frame to canvas
    await this.captureVideoFrame(canvas);

    this.setInstantFeedback(new CameraFeedback('Please wait...'));

    this.setCapturingAction(false);
    this.setCapturedAction(true);

    this.stopVideo();

    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Capture completed');
    }
  }

  private setInstantFeedback(feedback: CameraFeedback): void {
    this.setFeedbackAction(feedback);
  }

  private async getDeviceData(stream: MediaStream): Promise<IDeviceData> {
    const tracks = stream.getTracks();
    const devices = await navigator.mediaDevices.enumerateDevices();
    const settings = tracks[0].getSettings();
    const deviceInfo = devices.find((device) => device.deviceId === settings.deviceId);

    const deviceData = {
      deviceId: settings.deviceId,
      deviceName: deviceInfo?.label,
      frameRate: settings.frameRate ? Math.round(settings.frameRate) : 0,
      height: settings.height,
      width: settings.width,
    };

    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Device data collected:', deviceData);
    }

    return deviceData;
  }

  private async captureVideoFrame(canvas: HTMLCanvasElement): Promise<void> {
    if (!this.video) {
      return;
    }

    // Pause the video
    this.video.pause();

    // Set canvas dimensions to match video
    canvas.width = this.video.videoWidth;
    canvas.height = this.video.videoHeight;

    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Capturing video frame', {
        videoWidth: this.video.videoWidth,
        videoHeight: this.video.videoHeight,
      });
    }

    // Draw the current video frame to the canvas
    const ctx = canvas.getContext('2d');
    if (ctx) {
      ctx.drawImage(this.video, 0, 0, canvas.width, canvas.height);

      if (this.enableDebugLogging) {
        console.log('[SimpleFaceCaptureService] Frame drawn to canvas');
      }
    }
  }

  private async startVideoStream(): Promise<void> {
    if (this.enableDebugLogging) {
      console.log('[SimpleFaceCaptureService] Starting video stream');
    }

    this.stopVideoStream();

    this.videoStream = await navigator.mediaDevices
      .getUserMedia({
        video: {
          width: {
            min: 600,
            ideal: 1920,
            max: 3840,
          },
          height: {
            min: 400,
            ideal: 1080,
            max: 2160,
          },
        },
        audio: false,
      })
      .then(async (stream) => {
        if (this.enableDebugLogging) {
          console.log('[SimpleFaceCaptureService] Video stream obtained successfully');
        }

        const tracks = stream.getTracks();
        for (const track of tracks) {
          track.addEventListener('ended', this.handleCameraNotFound);
        }
        const deviceData = await this.getDeviceData(stream);
        this.setDeviceDataAction(deviceData);
        return stream;
      })
      .catch((error) => {
        if (this.enableDebugLogging) {
          console.error('[SimpleFaceCaptureService] Failed to get video stream:', error);
        }

        this.setInstantFeedback(
          new CameraFeedback(
            'Camera unavailable',
            'We are unable to access the camera at this time, try again later',
            true,
          ),
        );

        return null;
      });
  }

  private handleCameraNotFound = (): void => {
    this.setInstantFeedback(
      new CameraFeedback(
        'Camera unavailable',
        'We are unable to access the camera at this time, try again later',
        true,
      ),
    );
  };

  private stopVideoStream(): void {
    if (!this.videoStream) {
      return;
    }

    for (const track of this.videoStream.getTracks()) {
      track.stop();
    }

    this.videoStream = null;
  }
}
